import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import StatusBar from '@app/components/statusBar/statusBar';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Form, notification, Row, Col, Radio } from 'antd';
import { notificationController } from '@app/controllers/notificationController';
import {
  showPRProcess,
  editPRProcess,
  createPRProcess,
  storePRProcess,
  updatePRProcess,
} from '@app/api/prProcesses.api';
import { PRProcessRequest } from '@app/models/pr_processes/PRProcessRequest';

interface PRProcessFormProps {
  editable?: boolean;
  id?: string;
  pageTitle?: string;
  type?: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

const PRProcessForm: React.FC<PRProcessFormProps> = ({
  editable = false,
  id,
  pageTitle,
  type,
  setCurrentBreadcrumName,
}) => {
  const [processTypeOptions, setPRTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const [productOptions, setProductOptions] = useState<{ label: string; value: string }[]>([]);
  const [certificateOptions, setCertificateOptions] = useState<{ label: string; value: string }[]>([]);
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[]>([]);
  const { t } = useTranslation();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | null>(null);
  const [validityOptions, setValidityOptions] = useState<{ label: string; value: string }[]>([]);
  const [form] = Form.useForm();
  const hasFetchedRef = useRef(false);

  const [isLoading, setLoading] = useState(false);
  const pr_processId = id ? parseInt(id) : null;

  const setApiData = (res: any) => {
    form.setFieldsValue({
      pr_product_id: res?.pr_process?.pr_product_id,
      pr_product: res?.pr_process?.pr_product.name,
      pr_type: res?.pr_process?.pr_type,
      validity: res?.pr_process?.validity,
      validity_unit: res?.pr_process?.validity_unit,
      status: res?.pr_process?.status,
      is_appeal: Boolean(res?.pr_process?.is_appeal),
      has_application: Boolean(res?.pr_process?.has_application),
      application_price: res?.pr_process?.application_price,
      has_certificate: Boolean(res?.pr_process?.has_certificate),
      certificate_price: res?.pr_process?.certificate_price,
      certificate_layout: res?.pr_process?.certificate_layout,
    });
    setStatus(res.pr_process.status);
    setCurrentBreadcrumName(res?.pr_process?.id || 'New');
  };

  const mapOptions = (options: any[] = []) =>
    options?.map((item) => ({
      label: item.label,
      value: item.value,
    }));

  const setAllOptions = (res: any) => {
    setStatusOptions(mapOptions(res.status_options));
    setProductOptions(mapOptions(res.pr_product_options));
    setPRTypeOptions(mapOptions(res.pr_types_options));
    setCertificateOptions(mapOptions(res.certificate_options));
    setValidityOptions(mapOptions(res.pr_validity_unit_options));
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setLoading(true);
    if (pr_processId) {
      if (type === 'view') {
        showPRProcess(pr_processId)
          .then((res: any) => {
            setApiData(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to view PR Process', description: err.message });
            console.error(err);
          });
      } else if (type === 'edit') {
        editPRProcess(pr_processId)
          .then((res: any) => {
            setAllOptions(res);
            setApiData(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to edit PR Process', description: err.message });
            console.error(err);
          });
      }
    } else if (type === 'create') {
      createPRProcess()
        .then((res: any) => {
          setAllOptions(res);
          setLoading(false);
        })
        .catch((err: any) => {
          setLoading(false);
          notification.error({ message: 'Failed to fetch PR Process', description: err.message });
          console.error(err);
        });
    }
  }, []);

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" loading={isLoading} onClick={() => form.submit()} size="small">
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton
          onClick={() => navigate('/product-registration-processes')}
          style={{ marginRight: '8px' }}
          size="small"
        >
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton
          type="primary"
          onClick={() => navigate(`/edit-product-registration-process`, { state: { id } })}
          size="small"
        >
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, isLoading, editable, id, addHeaderButton, t, navigate, removeHeaderButton]);

  const handleSubmit = (values: PRProcessRequest) => {
    if (editable) {
      const submissionData = {
        ...values,
        application_price: values.has_application ? values.application_price || 0 : 0,
        certificate_price: values.has_certificate ? values.certificate_price || 0 : 0,
      };

      if (type === 'create') {
        storePRProcess(submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate(`/view-product-registration-process`, { state: { id: res?.data?.id } });
          })
          .catch((err: any) => {
            notificationController.error({ message: err.message });
            setLoading(false);
          });
      } else if (type === 'edit' && pr_processId) {
        updatePRProcess(pr_processId, submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate(`/view-product-registration-process`, { state: { id: id } });
          })
          .catch((err: any) => {
            notificationController.error({ message: `${err.message}` });
            setLoading(false);
          });
      }
    }
    console.log('Form Submitted');
  };

  return (
    <>
      <BaseRow gutter={[16, 16]} style={{ width: '100%' }}>
        <BaseCol span={24}>
          <StatusBar id={String(id)} status={status} />
        </BaseCol>
        <BaseCol span={24}>
          <BaseCard id="PRProcessForm" title={pageTitle} padding="1.25rem">
            <Form
              form={form}
              name="PRProcessForm"
              onFinish={handleSubmit}
              size="small"
              labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
              labelAlign="left"
              labelWrap
              wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
            >
              <Row gutter={16}>
                <Col xs={24} md={24} lg={12}>
                  <Form.Item label="Product">
                    {!editable ? (
                      <BaseInput readOnly value={form.getFieldValue('pr_product') || ''} />
                    ) : (
                      <Form.Item name="pr_product_id">
                        <BaseSelect placeholder="Select Product" options={productOptions} disabled={!editable} />
                      </Form.Item>
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} md={8} lg={12}>
                  <Form.Item
                    name="pr_type"
                    label="Process Type"
                    rules={[{ required: true, message: 'Process Type is required' }]}
                  >
                    {!editable ? (
                      <BaseInput readOnly value={form.getFieldValue('pr_type') || ''} />
                    ) : (
                      <BaseSelect placeholder="Select Process Type" options={processTypeOptions} disabled={!editable} />
                    )}
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={24} lg={12}>
                  <Form.Item
                    name="validity"
                    label="Validity"
                    rules={[{ required: true, message: 'Validity is required' }]}
                  >
                    <BaseInput placeholder="Enter Validity" readOnly={!editable} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={24} lg={12}>
                  <Form.Item
                    name="validity_unit"
                    label="Validity Unit"
                    rules={[{ required: true, message: 'Validity Unit is required' }]}
                  >
                    {!editable ? (
                      <BaseInput readOnly value={form.getFieldValue('validity_unit') || ''} />
                    ) : (
                      <BaseSelect placeholder="Select Validity Unit" options={validityOptions} disabled={!editable} />
                    )}
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={24} lg={12}>
                  <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Status is required' }]}>
                    {!editable ? (
                      <BaseInput readOnly value={form.getFieldValue('status') || ''} />
                    ) : (
                      <BaseSelect placeholder="Select Status" options={statusOptions} disabled={!editable} />
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} md={24} lg={12}>
                  <Form.Item name="is_appeal" label="Has Appeal?">
                    <Radio.Group disabled={!editable}>
                      <Radio value={true}>Yes</Radio>
                      <Radio value={false}>No</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={24} lg={12}>
                  <Form.Item name="has_application" label="Has Application?">
                    <Radio.Group disabled={!editable}>
                      <Radio value={true}>Yes</Radio>
                      <Radio value={false}>No</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
                <Col xs={24} md={24} lg={12}>
                  {form.getFieldValue('has_application') && (
                    <Form.Item name="application_price" label="Application Price">
                      <BaseInput placeholder="Enter Application Price" readOnly={!editable} />
                    </Form.Item>
                  )}
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24} md={24} lg={12}>
                  <Form.Item name="has_certificate" label="Has Certificate?">
                    <Radio.Group disabled={!editable}>
                      <Radio value={true}>Yes</Radio>
                      <Radio value={false}>No</Radio>
                    </Radio.Group>
                  </Form.Item>
                </Col>
              </Row>

              {form.getFieldValue('has_certificate') && (
                <Row gutter={16}>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item name="certificate_price" label="Certificate Price">
                      <BaseInput placeholder="Enter Certificate Price" readOnly={!editable} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={24} lg={12}>
                    <Form.Item name="certificate_layout" label="Certificate Layout">
                      {!editable ? (
                        <BaseInput readOnly value={form.getFieldValue('certificate_layout') || ''} />
                      ) : (
                        <BaseSelect
                          placeholder="Select Certificate Layout"
                          options={certificateOptions}
                          disabled={!editable}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              )}
            </Form>
          </BaseCard>
        </BaseCol>
      </BaseRow>
    </>
  );
};

export default PRProcessForm;
